import React from 'react';
import { View, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity } from 'react-native';
import { Text, Card, TextInput, Button, Checkbox, RadioButton, List, FAB } from 'react-native-paper';
import { useRoute, useNavigation, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useSelector, useDispatch } from 'react-redux';
import { ReviewStackParamList, ResultsStackParamList } from '../../navigation/AppNavigator';
import { RootState, AppDispatch } from '../../store';
import { updateReviewResponse, updateSubmittedReviewResponse, updateLocalResponse, fetchReviewById, fetchOnDemandItems, updateReviewNotes, updateReviewOnDemandProjects, fetchReviewItemDetail } from '../../store/slices/reviewSlice';
import { colors, spacing, typography, shadows } from '../../constants/theme';
import StylusTextInput from '../../components/StylusTextInput';

import { ResponseStatus } from '../../types';
import type { ReviewFinding } from '../../types';
import { RoomDropdown } from '../../components/RoomDropdown';
import { FloorDropdown } from '../../components/FloorDropdown';
import { DynamicForm } from '../../components/DynamicForm';
import { Platform } from 'react-native';
import { api } from '../../services/api';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { QuestionListModal } from '../../components/QuestionListModal';
import { updateOnDemandItem } from '../../store/slices/reviewSlice';
import { useSync } from '../../hooks/useSync';
import { isMultiplicityQuestion } from '../../utils/questionHelpers';
import { MAX_PHOTOS_PER_UPLOAD } from '../../constants/photoLimits';
import { selectBestResponseForItem } from '../../services/reviewResponseSelector';

// Platform-specific PhotoCapture import
import { PhotoCapture } from '../../components/PhotoCapture';
import { ExpandableFAB } from '../../components/ExpandableFAB';
import { buildHamburgerActions } from '../../utils/buildHamburgerActions';
import { GeneralNotesModal } from '../../components/GeneralNotesModal';
import { OnDemandProjectsModal } from '../../components/OnDemandProjectsModal';
import { ReviewSearchModal } from '../../components/ReviewSearchModal';
import { isDetoxTestControlsEnabled, shouldSkipOfflineCache } from '../../config/environments';
import { toNavigableReviewItemId } from '../../utils/reviewItemSearch';
import { FindingEditor } from '../../components/FindingEditor';
import { getFinding, replaceFinding, resolveFindingClassification } from '../../utils/reviewFindings';
import { reviewService } from '../../services/reviewService';
import { getFindingSeverityAppearance } from '../../constants/findingSeverity';

type ReviewRouteProps = RouteProp<ReviewStackParamList, 'ItemDetail'>;
type ResultsRouteProps = RouteProp<ResultsStackParamList, 'ItemDetail'>;
type RouteProps = ReviewRouteProps | ResultsRouteProps;

type ReviewNavigationProps = StackNavigationProp<ReviewStackParamList, 'ItemDetail'>;
type ResultsNavigationProps = StackNavigationProp<ResultsStackParamList, 'ItemDetail'>;
type NavigationProps = ReviewNavigationProps | ResultsNavigationProps;

const getServerPhotoId = (photo: any): number | null => {
  const photoId = Number(photo?.backendId ?? photo?.id);
  return Number.isSafeInteger(photoId) && photoId > 0 ? photoId : null;
};

const hasOfflineLocalPhoto = (photo: any): boolean =>
  photo?.isOfflineOnly === true &&
  typeof photo?.uri === 'string' &&
  photo.uri.length > 0 &&
  !/^https?:\/\//i.test(photo.uri);

const ItemDetailScreenContent: React.FC = () => {
  const route = useRoute<RouteProps>();
  const navigation = useNavigation<NavigationProps>();
  const dispatch = useDispatch<AppDispatch>();
  const { isOnline } = useSync();
  
  // Wrap everything in try-catch to prevent any crashes
  let routeParams;
  try {
    routeParams = route.params || {};
  } catch (error) {
    console.error('[ItemDetailScreen] Route params error:', error);
    routeParams = {};
  }
  
  const { reviewId, itemId, categoryName } = routeParams;

  // Parse to numbers to handle web's string params vs Redux's numeric IDs
  const numericReviewId = Number(reviewId);
  const numericItemId = Number(itemId);

  let review;
  let isLoadingReview = false;
  try {
    review = useSelector((state: RootState) =>
      state.review.reviews.find(r => r.id === numericReviewId)
    );
    isLoadingReview = useSelector((state: RootState) => state.review.isLoadingReview);
  } catch (error) {
    console.error('[ItemDetailScreen] Redux selector error:', error);
    review = null;
  }

  const reduxResponse = React.useMemo(() => {
    if (!review?.responses) {
      return undefined;
    }
    return selectBestResponseForItem(review.responses, numericItemId);
  }, [review, numericItemId]);
  const responseScopeKey = `${numericReviewId}:${numericItemId}`;
  const [presentationResponse, setPresentationResponse] = React.useState<{
    scopeKey: string;
    response: any;
  } | null>(null);
  const response = reduxResponse || (
    presentationResponse?.scopeKey === responseScopeKey
      ? presentationResponse.response
      : undefined
  );

  // Add safety check for params
  React.useEffect(() => {
    if (!reviewId || !itemId) {
      setTimeout(() => {
        try {
          navigation.goBack();
        } catch (navError) {
          console.error('[ItemDetailScreen] Navigation error:', navError);
        }
      }, 100);
    }
  }, [reviewId, itemId, navigation]);

  const reviewHasTemplate = Boolean(review?.template?.categories && review.template.categories.length > 0);
  const lastItemPhotoHydrationSignatureRef = React.useRef<string | null>(null);
  const responsePhotoHydrationInFlightRef = React.useRef<string | null>(null);
  const photoUrlScreenMountedRef = React.useRef(true);
  const [presignedPhotoUrls, setPresignedPhotoUrls] = React.useState<Record<number, string>>({});
  const previousPhotoUrlOnlineRef = React.useRef(isOnline);
  const [photoUrlRefreshGeneration, setPhotoUrlRefreshGeneration] = React.useState(
    isOnline ? 1 : 0
  );

  const backendPhotoIds = Array.from(new Set<number>(
    (response?.photos || []).flatMap((photo: any) => {
      const photoId = getServerPhotoId(photo);
      return photoId === null ? [] : [photoId];
    })
  )).sort((left, right) => left - right);

  const needsResponseData = Boolean(review) && !response;
  const reconnectRefreshPending = isOnline && !previousPhotoUrlOnlineRef.current;
  const responsePhotoHydrationKey = `${numericReviewId}:${numericItemId}:${photoUrlRefreshGeneration}`;
  const hydratedResponsePending = Boolean(response) &&
    responsePhotoHydrationInFlightRef.current === responsePhotoHydrationKey;
  const itemPhotoHydrationSignature = !isOnline || reconnectRefreshPending || hydratedResponsePending
    ? null
    : needsResponseData
      ? `${numericReviewId}:${numericItemId}:missing-response:${photoUrlRefreshGeneration}`
      : backendPhotoIds.length > 0
        ? `${numericReviewId}:${numericItemId}:${response?.id ?? response?.tempId ?? 'unknown'}:${photoUrlRefreshGeneration}:${backendPhotoIds.join(',')}`
        : null;

  React.useEffect(() => {
    if (isOnline && !previousPhotoUrlOnlineRef.current) {
      setPhotoUrlRefreshGeneration(generation => generation + 1);
    }
    previousPhotoUrlOnlineRef.current = isOnline;
  }, [isOnline]);

  React.useEffect(() => () => {
    photoUrlScreenMountedRef.current = false;
  }, []);

  React.useEffect(() => {
    if (!numericReviewId) {
      return;
    }

    const needsHydration = !review || !reviewHasTemplate;
    if (!needsHydration) {
      return;
    }

    if (isOnline) {
      dispatch(fetchReviewById({ reviewId: numericReviewId, includeTemplate: true }));
    }
  }, [dispatch, numericReviewId, review, reviewHasTemplate, isOnline]);

  React.useEffect(() => {
    if (!numericReviewId || !numericItemId) {
      return;
    }

    // When deep-linking into an item after app restart, let the full review
    // hydration path run first. That path reconciles server data with the
    // local offline journal; fetching a single stale server item first can
    // briefly overwrite the restored local response in Redux.
    if (!itemPhotoHydrationSignature) {
      setIsItemLoading(false);
      setItemLoadError(null);
      return;
    }

    if (lastItemPhotoHydrationSignatureRef.current === itemPhotoHydrationSignature) {
      return;
    }
    lastItemPhotoHydrationSignatureRef.current = itemPhotoHydrationSignature;

    let isActive = true;
    setItemLoadError(null);
    setIsItemLoading(true);

    if (needsResponseData) {
      responsePhotoHydrationInFlightRef.current = responsePhotoHydrationKey;
    }

    const hydrationRequest = needsResponseData
      ? dispatch(fetchReviewItemDetail({
          reviewId: numericReviewId,
          itemId: numericItemId,
          persistResponse: false,
        }))
          .unwrap()
          .then(result => {
            const hydratedResponse = result?.response;
            const hydratedPhotos = hydratedResponse?.photos || [];
            if (hydratedResponse && photoUrlScreenMountedRef.current) {
              setPresentationResponse({
                scopeKey: responseScopeKey,
                response: hydratedResponse,
              });
            }
            const hydratedPhotoIds = Array.from(new Set<number>(
              hydratedPhotos.flatMap((photo: any) => {
                const photoId = getServerPhotoId(photo);
                return photoId === null ? [] : [photoId];
              })
            )).sort((left, right) => left - right);
            const hydratedPresignedPhotoIds = new Set(
              hydratedPhotos.flatMap((photo: any) => {
                const photoId = getServerPhotoId(photo);
                return photoId !== null && photo.presignedUrl ? [photoId] : [];
              })
            );
            const hydrationIncludesAllPresignedUrls = hydratedPhotoIds.length > 0 &&
              hydratedPhotoIds.every(photoId => hydratedPresignedPhotoIds.has(photoId));
            const hydrationPresignFailed = result?.photoPresignFailed === true;

            if (
              hydratedResponse &&
              (hydrationIncludesAllPresignedUrls || hydrationPresignFailed)
            ) {
              lastItemPhotoHydrationSignatureRef.current =
                `${numericReviewId}:${numericItemId}:${hydratedResponse.id ?? hydratedResponse.tempId ?? 'unknown'}:${photoUrlRefreshGeneration}:${hydratedPhotoIds.join(',')}`;

              if (hydrationIncludesAllPresignedUrls && photoUrlScreenMountedRef.current) {
                setPresignedPhotoUrls(currentUrls => {
                  let didAddUrl = false;
                  const nextUrls = { ...currentUrls };

                  for (const photo of hydratedPhotos) {
                    const photoId = getServerPhotoId(photo);
                    if (
                      photoId !== null &&
                      photo.presignedUrl &&
                      nextUrls[photoId] !== photo.presignedUrl
                    ) {
                      nextUrls[photoId] = photo.presignedUrl;
                      didAddUrl = true;
                    }
                  }

                  return didAddUrl ? nextUrls : currentUrls;
                });
              }
            }

            return result;
          })
      : reviewService.getPhotoPresignedUrls(backendPhotoIds).then(presignedPhotos => {
          if (!isActive || presignedPhotos.length === 0) {
            return;
          }

          // Signed URLs are presentation-only. Writing them through the
          // response reducer mutates timestamps and can select a different
          // duplicate response or reset locally edited question photos.
          setPresignedPhotoUrls(currentUrls => {
            let didAddUrl = false;
            const nextUrls = { ...currentUrls };

            for (const photo of presignedPhotos) {
              const photoId = Number(photo.photoId);
              if (
                Number.isSafeInteger(photoId) &&
                photoId > 0 &&
                photo.presignedUrl &&
                nextUrls[photoId] !== photo.presignedUrl
              ) {
                nextUrls[photoId] = photo.presignedUrl;
                didAddUrl = true;
              }
            }

            return didAddUrl ? nextUrls : currentUrls;
          });
        });

    Promise.resolve(hydrationRequest)
      .catch((error: any) => {
        if (!isActive) return;
        const message = error?.message || 'Failed to load item details';
        setItemLoadError(message);
      })
      .finally(() => {
        if (responsePhotoHydrationInFlightRef.current === responsePhotoHydrationKey) {
          responsePhotoHydrationInFlightRef.current = null;
        }
        if (isActive) {
          setIsItemLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [
    dispatch,
    itemPhotoHydrationSignature,
    numericItemId,
    numericReviewId,
    responseScopeKey,
    responsePhotoHydrationKey,
  ]);

  // Helper function to recursively find items in categories and subcategories
  const findItemInCategories = React.useCallback((categories: any[], targetItemId: number): any => {
    if (!categories || !Array.isArray(categories)) {
      return null;
    }
    
    for (const category of categories) {
      if (!category) continue;
      
      // Check items in this category
      if (category.items && Array.isArray(category.items)) {
        const foundItem = category.items.find((item: any) => item && item.id === targetItemId);
        if (foundItem) return foundItem;
      }
      
      // Check subcategories recursively
      if (category.subcategories && Array.isArray(category.subcategories)) {
        const foundInSubcategory = findItemInCategories(category.subcategories, targetItemId);
        if (foundInSubcategory) return foundInSubcategory;
      }
    }
    return null;
  }, []);

  // Get on-demand items from Redux store
  const onDemandItems = useSelector((state: RootState) =>
    state.review.onDemandItems?.[numericReviewId] || []
  );

  const item = React.useMemo(() => {
    // First check if this is an on-demand item
    const onDemandItem = onDemandItems.find((item: any) => item.id === numericItemId);
    if (onDemandItem) {
      // Ensure questionsSchema is properly structured
      let questionsSchema = onDemandItem.questionsSchema;
      
      // Handle if questionsSchema is a string (from JSON storage)
      if (typeof questionsSchema === 'string') {
        try {
          questionsSchema = JSON.parse(questionsSchema);
        } catch (e) {
          console.error('Failed to parse questionsSchema:', e);
          questionsSchema = { fields: [] };
        }
      }
      
      // Ensure fields is an array
      if (!questionsSchema || !Array.isArray(questionsSchema.fields)) {
        questionsSchema = { fields: questionsSchema?.fields || [] };
      }
      
      return {
        ...onDemandItem,
        questionsSchema,
        isOnDemand: true
      };
    }
    
    // Try to find item in template categories
    if (review?.template?.categories) {
      const foundItem = findItemInCategories(review.template.categories, numericItemId);
      if (foundItem) {
        // Also ensure template items have proper questionsSchema
        let questionsSchema = foundItem.questionsSchema;

        // Handle if questionsSchema is a string (from JSON storage)
        if (typeof questionsSchema === 'string') {
          try {
            questionsSchema = JSON.parse(questionsSchema);
          } catch (e) {
            console.error('Failed to parse template questionsSchema:', e);
            questionsSchema = { fields: [] };
          }
        }

        // Ensure fields is an array
        if (!questionsSchema || !Array.isArray(questionsSchema?.fields)) {
          // If questionsSchema exists but fields is not an array, try to preserve it
          if (questionsSchema && typeof questionsSchema === 'object') {
            questionsSchema = { ...questionsSchema, fields: questionsSchema.fields || [] };
          } else {
            questionsSchema = { fields: [] };
          }
        }

        return {
          ...foundItem,
          questionsSchema
        };
      }
    }

    // Fallback: Create a basic item object if we can't find it
    // This prevents crashes when template data isn't fully loaded
    return {
      id: numericItemId,
      name: categoryName || 'Item',
      description: '',
      questionsSchema: { fields: [] }
    };
  }, [review?.template?.categories, numericItemId, categoryName, findItemInCategories, onDemandItems]);

  const findingClassification = React.useMemo(
    () => resolveFindingClassification(item.questionsSchema, review?.createdAt),
    [item.questionsSchema, review?.createdAt]
  );

  // Track response IDs in local state for immediate availability (before Redux updates)
  const [localResponseId, setLocalResponseId] = React.useState<number | undefined>(response?.id);
  const [currentResponseTempId, setCurrentResponseTempId] = React.useState<string | undefined>(response?.tempId);
  const [isCreatingResponse, setIsCreatingResponse] = React.useState(false);

  // ALL hooks must be declared BEFORE any conditional returns to follow Rules of Hooks
  const [isPresent, setIsPresent] = React.useState(response?.isPresent ?? true);
  const [notes, setNotes] = React.useState(response?.notes || '');
  const [responseData, setResponseData] = React.useState(response?.responseData || {});
  const [roomId, setRoomId] = React.useState<number | undefined>(response?.roomId);
  const [floor, setFloor] = React.useState<string | undefined>(response?.floor);
  const [roomSelections, setRoomSelections] = React.useState<Record<string, number | undefined>>({});
  const [questionPhotos, setQuestionPhotos] = React.useState<Record<string, any[]>>({});
  const [photos, setPhotos] = React.useState(response?.photos || []);
  const [findings, setFindings] = React.useState<ReviewFinding[]>(response?.findings || []);
  const itemFindingAppearance = findingClassification.mode === 'item'
    ? getFindingSeverityAppearance(getFinding(findings, null)?.severity)
    : undefined;
  const [isSaving, setIsSaving] = React.useState(false);
  const [isComponentReady, setIsComponentReady] = React.useState(false);
  const [hasUserInteracted, setHasUserInteracted] = React.useState(false);
  const [showQuestionEditor, setShowQuestionEditor] = React.useState(false);
  const [isAutoSaving, setIsAutoSaving] = React.useState(false);
  const [isItemLoading, setIsItemLoading] = React.useState(false);
  const [itemLoadError, setItemLoadError] = React.useState<string | null>(null);
  const placeholderResponsePromiseRef = React.useRef<Promise<void> | null>(null);
  const hasUserInteractedRef = React.useRef(false);
  const isSavingRef = React.useRef(false);
  const isAutoSavingRef = React.useRef(false);
  const photoResponseId = localResponseId || response?.id || 0;
  const photoResponseTempId = currentResponseTempId || response?.tempId;
  const canQueuePhotos = Boolean(photoResponseId || photoResponseTempId);
  const withPresentationPhotoUrl = React.useCallback((photo: any) => {
    if (!isOnline && hasOfflineLocalPhoto(photo)) {
      return photo.requiresOnline === true
        ? { ...photo, requiresOnline: false }
        : photo;
    }

    const photoId = getServerPhotoId(photo);
    const presignedUrl = (photoId === null
      ? undefined
      : presignedPhotoUrls[photoId]) || photo.presignedUrl;

    if (!presignedUrl) {
      return photo;
    }

    return {
      ...photo,
      uri: presignedUrl,
      presignedUrl,
      requiresOnline: true,
    };
  }, [isOnline, presignedPhotoUrls]);
  const presentationPhotos = React.useMemo(
    () => photos.map(withPresentationPhotoUrl),
    [photos, withPresentationPhotoUrl]
  );
  const presentationQuestionPhotos = React.useMemo(
    () => Object.fromEntries(
      Object.entries(questionPhotos).map(([questionId, questionPhotoList]) => [
        questionId,
        questionPhotoList.map(withPresentationPhotoUrl),
      ])
    ),
    [questionPhotos, withPresentationPhotoUrl]
  );

  // FAB-related state
  const [showGeneralNotesModal, setShowGeneralNotesModal] = React.useState(false);
  const [showOnDemandProjectsModal, setShowOnDemandProjectsModal] = React.useState(false);
  const [showSearchModal, setShowSearchModal] = React.useState(false);
  const [isSavingNotes, setIsSavingNotes] = React.useState(false);
  const [isSavingOnDemandProjects, setIsSavingOnDemandProjects] = React.useState(false);

  // Get network status for offline-aware save behavior
  // Track if component is mounted to prevent navigation after unmount
  const isMountedRef = React.useRef(true);

  // Track if navigation is in progress to prevent concurrent navigation attempts
  const isNavigatingRef = React.useRef(false);

  // Track sync failures locally without triggering Redux updates during navigation
  const syncFailureCountRef = React.useRef(0);

  // Track last auto-save time for debouncing beforeRemove events
  const lastAutoSaveTimeRef = React.useRef(0);
  const AUTO_SAVE_DEBOUNCE_MS = 1000;

  const markUserInteracted = React.useCallback(() => {
    hasUserInteractedRef.current = true;
    setHasUserInteracted(true);
  }, []);

  const clearUserInteracted = React.useCallback(() => {
    hasUserInteractedRef.current = false;
    setHasUserInteracted(false);
  }, []);

  // Keep photos with file objects for display
  const handlePhotosChange = React.useCallback((newPhotos: any[]) => {
    setPhotos(newPhotos);
  }, []);

  // Enhanced photo deduplication - checks multiple fields to prevent duplicates
  // Especially important for offline scenarios where timing can cause races
  const isPhotoAlreadyPresent = React.useCallback((newPhoto: any, existingPhotos: any[]) => {
    return existingPhotos.some((existing: any) => {
      // Match by any of: id, tempId, uri, fileName
      if (newPhoto.id && existing.id && newPhoto.id === existing.id) return true;
      if (newPhoto.tempId && existing.tempId && newPhoto.tempId === existing.tempId) return true;
      if (newPhoto.uri && existing.uri && newPhoto.uri === existing.uri) return true;
      if (newPhoto.fileName && existing.fileName && newPhoto.fileName === existing.fileName) return true;

      // Match by originalUri in metadata (handles queue service filename change)
      const newOriginalUri = newPhoto.metadata?.originalUri;
      const existingOriginalUri = existing.metadata?.originalUri;

      // Both have originalUri and they match
      if (newOriginalUri && existingOriginalUri && newOriginalUri === existingOriginalUri) return true;

      // Queue photo's originalUri matches original photo's uri
      if (newOriginalUri && existing.uri && newOriginalUri === existing.uri) return true;

      // Original photo's uri matches queue photo's originalUri (reverse)
      if (newPhoto.uri && existingOriginalUri && newPhoto.uri === existingOriginalUri) return true;

      return false;
    });
  }, []);

  // Helper function to determine source stack and navigate accordingly
  const navigateBack = React.useCallback(() => {
    // Don't navigate if component is unmounted or already navigating
    if (!isMountedRef.current || isNavigatingRef.current) return;

    // Set navigation flag to prevent concurrent navigation attempts
    isNavigatingRef.current = true;

    const state = navigation.getState();
    const currentRoute = state.routes[state.index];

    // Check if we're in Results stack by looking at the route names
    const isFromResults = (state.routeNames as string[] | undefined)?.includes('ResultsList') ||
                         currentRoute?.name === 'ItemDetail' &&
                         state.routes.some(route => route.name === 'ResultDetail');

    if (isFromResults) {
      // Navigate back to ResultDetail in Results stack
      (navigation as ResultsNavigationProps).navigate('ResultDetail', { reviewId: numericReviewId });
    } else {
      // Default to goBack for Reviews stack
      navigation.goBack();
    }
  }, [navigation, numericReviewId]);

  // Auto-save function for back navigation
  const handleAutoSaveAndNavigate = React.useCallback(async () => {
    if (!isMountedRef.current || isAutoSavingRef.current) return;

    isAutoSavingRef.current = true;
    setIsAutoSaving(true);

    // Helper function to clean photos
    // Removes non-serializable File objects and temporary IDs before saving to Redux/server
    const cleanPhotos = (photoArray: any[]) =>
      photoArray.map((photo: any) => {
        const { file, tempId, ...serializable } = photo;
        return serializable;
      });

    // Helper function to clean question photos
    const cleanQuestionPhotos = (questionPhotoObj: Record<string, any[]>) => {
      const cleaned: Record<string, any[]> = {};
      Object.entries(questionPhotoObj).forEach(([key, photoArray]) => {
        cleaned[key] = cleanPhotos(photoArray);
      });
      return cleaned;
    };

    // Prepare draft data - use conditional logic since review might be null
    // CRITICAL: Filter out question photos to prevent duplication
    const itemLevelPhotosOnly = photos.filter((p: any) =>
      !p.metadata?.questionId && !p.questionId
    );

    const draftData = {
      isPresent,
      notes,
      responseData: {
        ...responseData,
        ...roomSelections,
      },
      roomId,
      floor,
      photos: cleanPhotos(itemLevelPhotosOnly),
      questionPhotos: cleanQuestionPhotos(questionPhotos),
      findings,
      status: ResponseStatus.IN_PROGRESS,
    };

    try {
      // Update local state immediately (this ensures data isn't lost even if server fails)
      dispatch(updateLocalResponse({ reviewId: numericReviewId, itemId: numericItemId, data: draftData }));

      // Await dispatch to prevent state updates during navigation (beforeRemove path only)
      const isSubmitted = review?.status === 'submitted';
      await dispatch(
        isSubmitted
          ? updateSubmittedReviewResponse({ reviewId: numericReviewId, itemId: numericItemId, data: draftData })
          : updateReviewResponse({ reviewId: numericReviewId, itemId: numericItemId, data: draftData })
      ).unwrap();

      clearUserInteracted();
    } catch (error) {
      // Log locally without triggering Redux updates during navigation
      syncFailureCountRef.current += 1;
    } finally {
      // Always clear isAutoSaving, even on error
      isAutoSavingRef.current = false;
      if (isMountedRef.current) {
        setIsAutoSaving(false);
      }
    }

    // Navigate after dispatch completes
    if (isMountedRef.current && !isNavigatingRef.current) {
      navigateBack();
    }
  }, [
    isPresent, notes, responseData, roomSelections, roomId, floor,
    photos, questionPhotos, findings, numericItemId, numericReviewId, review?.status, dispatch, navigateBack, clearUserInteracted
  ]);

  // Create placeholder response if one doesn't exist (for photo queueing).
  // Wait for full review hydration first; creating this row during a deep-link
  // relaunch can overwrite a pending offline response before reconciliation runs.
  React.useEffect(() => {
    const canCreatePlaceholder =
      Boolean(review) &&
      reviewHasTemplate &&
      !isLoadingReview &&
      !response &&
      !responsePhotoHydrationInFlightRef.current &&
      !isCreatingResponse &&
      !localResponseId &&
      numericReviewId &&
      numericItemId;

    if (canCreatePlaceholder) {
      setIsCreatingResponse(true);

      const createPlaceholderResponse = async () => {
        try {
          const result = await dispatch(
            updateReviewResponse({
              reviewId: numericReviewId,
              itemId: numericItemId,
              data: {
                isPresent: true,
                status: ResponseStatus.NOT_STARTED,
                responseData: {},
                notes: '',
              },
              optimistic: false, // Don't update Redux optimistically, we need the real result
              enableAutoSave: false,
              syncStatus: 'pending',
            })
          ).unwrap();

          // Capture IDs from result for immediate use (before Redux updates)
          if (result) {
            setLocalResponseId(result.id);
            setCurrentResponseTempId(result.tempId);
          }
        } catch (error) {
          console.error('[ItemDetailScreen] Failed to create placeholder response:', error);
        } finally {
          setIsCreatingResponse(false);
        }
      };

      const placeholderPromise = createPlaceholderResponse();
      placeholderResponsePromiseRef.current = placeholderPromise;
      placeholderPromise.finally(() => {
        if (placeholderResponsePromiseRef.current === placeholderPromise) {
          placeholderResponsePromiseRef.current = null;
        }
      });
    } else if (response) {
      // Update local state when response comes from Redux
      setLocalResponseId(response.id);
      setCurrentResponseTempId(response.tempId);
    }
  }, [
    response,
    isCreatingResponse,
    numericReviewId,
    numericItemId,
    dispatch,
    localResponseId,
    currentResponseTempId,
    review,
    reviewHasTemplate,
    isLoadingReview,
  ]);

  // Delay component readiness to prevent iOS modal crashes
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsComponentReady(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Update local state when response changes (e.g., after API calls)
  // Only update if user hasn't interacted yet to prevent overriding user changes
  React.useEffect(() => {
    if (response && !hasUserInteractedRef.current) {
      setIsPresent(response.isPresent ?? true);
      setNotes(response.notes || '');
      setResponseData(response.responseData || {});
      setRoomId(response.roomId);
      setFloor(response.floor);
      setFindings(response.findings || []);

      // Separate item-level photos from question-specific photos
      // CRITICAL: Filter symmetrically to prevent duplication
      if (response.photos && Array.isArray(response.photos)) {
        const itemLevelPhotos: any[] = [];
        const photosByQuestion: Record<string, any[]> = {};

        // Single pass: separate photos by presence of questionId
        for (const photo of response.photos) {
          const questionId = photo.metadata?.questionId || photo.questionId;
          const imageUri = hasOfflineLocalPhoto(photo)
            ? photo.uri
            : photo.presignedUrl || photo.s3Url || photo.uri;

          const mappedPhoto = {
            ...photo,
            uri: imageUri,
            uploaded: true
          };

          if (questionId) {
            // Photo belongs to a specific question
            if (!photosByQuestion[questionId]) {
              photosByQuestion[questionId] = [];
            }
            photosByQuestion[questionId].push(mappedPhoto);
          } else {
            // Photo is item-level (generic)
            itemLevelPhotos.push(mappedPhoto);
          }
        }

        // Update question photos
        setQuestionPhotos(photosByQuestion);

        // Merge item-level photos with any existing local photos (e.g., queued)
        // Use enhanced deduplication (checks id, tempId, uri, fileName)
        setPhotos((currentPhotos) => {
          if (itemLevelPhotos.length === 0) {
            return currentPhotos; // Keep current photos if Redux has none
          }

          // Filter out Redux photos that are already present in current photos
          // Uses multi-field matching to catch optimistic updates
          const uniqueReduxPhotos = itemLevelPhotos.filter(
            (p: any) => !isPhotoAlreadyPresent(p, currentPhotos)
          );

          // Merge: keep current photos (includes queued), add unique Redux photos
          return [...currentPhotos, ...uniqueReduxPhotos];
        });
      }
    }
  }, [response, isPhotoAlreadyPresent]);
  
  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Auto-save draft when back button is pressed
  React.useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (e) => {
      // Debounce rapid beforeRemove events
      const now = Date.now();
      if (now - lastAutoSaveTimeRef.current < AUTO_SAVE_DEBOUNCE_MS) {
        return;
      }

      // If we're already saving, auto-saving, navigating, or no user interaction, let navigation proceed
      if (isSavingRef.current || isAutoSavingRef.current || isNavigatingRef.current || !hasUserInteractedRef.current || !isMountedRef.current) {
        return;
      }

      // Update debounce timestamp
      lastAutoSaveTimeRef.current = now;

      // Prevent default navigation
      e.preventDefault();

      // Auto-save as draft and then navigate
      handleAutoSaveAndNavigate();
    });

    return unsubscribe;
  }, [navigation, handleAutoSaveAndNavigate]);

  // Clear navigation guard and debounce timer when screen loses focus
  React.useEffect(() => {
    const unsubscribe = navigation.addListener('blur', () => {
      // Reset navigation guard for next visit
      isNavigatingRef.current = false;
      // Reset debounce timer for immediate re-entry on next visit
      lastAutoSaveTimeRef.current = 0;
    });

    return unsubscribe;
  }, [navigation]);

  const handleQuestionPhotosChange = React.useCallback((questionId: string, photos: any[]) => {
    setQuestionPhotos(prev => ({
      ...prev,
      [questionId]: photos,
    }));
  }, []);

  const handleRoomSelect = React.useCallback((selectedRoomId: number | undefined, itemIndex?: number) => {
    if (itemIndex !== undefined) {
      // Handle room selection for multiple items
      setRoomSelections(prev => ({
        ...prev,
        [`roomId_item_${itemIndex}`]: selectedRoomId
      }));
    } else {
      // Handle single room selection (fallback)
      setRoomId(selectedRoomId);
    }
  }, []);

  const handleFloorSelect = React.useCallback((selectedFloor: string | undefined, itemIndex?: number) => {
    if (itemIndex !== undefined) {
      // Handle floor selection for multiple items - store in responseData
      setResponseData(prev => ({
        ...prev,
        [`floor_item_${itemIndex}`]: selectedFloor
      }));
    } else {
      // Handle single floor selection
      setFloor(selectedFloor);
    }
    markUserInteracted();
  }, [markUserInteracted]);

  // Check if we have multiple items (quantity > 1)
  const hasMultipleItems = React.useCallback(() => {
    if (!item?.questionsSchema?.fields) return false;

    const multiplicityFields = item.questionsSchema.fields.filter(isMultiplicityQuestion);

    if (multiplicityFields.length === 0) return false;

    const primaryQuantityField = multiplicityFields[0];
    const quantity = parseInt(responseData[primaryQuantityField.id] || '0', 10);
    return quantity > 1;
  }, [item?.questionsSchema?.fields, responseData]);

  const handleUpdateQuestions = React.useCallback(async (questions: any[]) => {
    if (!item.isOnDemand) return;
    
    try {
      // Update the on-demand item with new questions
      await dispatch(updateOnDemandItem({
        reviewId: numericReviewId,
        itemId: item.id,
        data: {
          questionsSchema: { fields: questions }
        }
      })).unwrap();

      setShowQuestionEditor(false);

      // Don't refetch - Redux will update from thunk payload
      // This avoids unnecessary network request when offline
    } catch (error) {
      console.error('❌ Failed to update questions:', error);
    }
  }, [item.isOnDemand, item.id, numericReviewId, dispatch]);

  // Handler for saving general notes
  const handleSaveGeneralNotes = React.useCallback(async (notes: string) => {
    setIsSavingNotes(true);
    try {
      await dispatch(updateReviewNotes({
        reviewId: numericReviewId,
        notes
      })).unwrap();
      setShowGeneralNotesModal(false);
    } catch (error) {
      console.error('Failed to update notes:', error);
    } finally {
      setIsSavingNotes(false);
    }
  }, [dispatch, numericReviewId]);

  const handleSaveOnDemandProjects = React.useCallback(async (onDemandProjects: string) => {
    setIsSavingOnDemandProjects(true);
    try {
      await dispatch(updateReviewOnDemandProjects({
        reviewId: numericReviewId,
        onDemandProjects
      })).unwrap();
      setShowOnDemandProjectsModal(false);
    } catch (error) {
      console.error('Failed to update On Demand Projects:', error);
    } finally {
      setIsSavingOnDemandProjects(false);
    }
  }, [dispatch, numericReviewId]);

  // Save without navigating - used when we want to save but handle navigation separately
  const saveWithoutNavigating = React.useCallback(async (): Promise<boolean> => {
    if (!isMountedRef.current) {
      return false;
    }

    if (placeholderResponsePromiseRef.current) {
      await placeholderResponsePromiseRef.current.catch(() => {});
    }

    if (!isMountedRef.current || isSavingRef.current) {
      return false;
    }

    isSavingRef.current = true;
    setIsSaving(true);

    try {
      // CRITICAL: Filter out question photos to prevent duplication
      const itemLevelPhotosOnly = photos.filter((p: any) =>
        !p.metadata?.questionId && !p.questionId
      );

      // Clean photos to remove non-serializable File objects before saving
      const cleanedPhotos = itemLevelPhotosOnly.map((photo: any) => {
        const { file, ...serializable} = photo;
        return serializable;
      });

      // Clean question photos too
      const cleanedQuestionPhotos: Record<string, any[]> = {};
      Object.entries(questionPhotos).forEach(([key, photoArray]) => {
        cleanedQuestionPhotos[key] = photoArray.map((photo: any) => {
          const { file, ...serializable } = photo;
          return serializable;
        });
      });

      const updatedResponse = {
        isPresent,
        notes,
        responseData: {
          ...responseData,
          ...roomSelections,
        },
        roomId,
        floor,
        photos: cleanedPhotos,
        questionPhotos: cleanedQuestionPhotos,
        findings,
        status: ResponseStatus.COMPLETED,
      };

      // Update local state immediately (optimistic update)
      dispatch(updateLocalResponse({ reviewId: numericReviewId, itemId: numericItemId, data: updatedResponse }));

      const isSubmitted = review?.status === 'submitted';

      // Wait for server confirmation
      await dispatch(
        isSubmitted
          ? updateSubmittedReviewResponse({ reviewId: numericReviewId, itemId: numericItemId, data: updatedResponse })
          : updateReviewResponse({ reviewId: numericReviewId, itemId: numericItemId, data: updatedResponse })
      ).unwrap();

      clearUserInteracted();
      return true;
    } catch (error) {
      console.warn('[ItemDetail] Server update failed:', error);
      syncFailureCountRef.current += 1;
      throw error; // Re-throw so caller knows save failed
    } finally {
      isSavingRef.current = false;
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [photos, questionPhotos, findings, isPresent, notes, responseData, roomSelections, roomId, floor, numericItemId, dispatch, numericReviewId, review?.status, clearUserInteracted]);

  const handleSave = React.useCallback(async () => {
    // Save then navigate back
    try {
      const didSave = await saveWithoutNavigating();

      // Navigate back after successful save
      if (didSave && isMountedRef.current && !isNavigatingRef.current) {
        navigateBack();
      }
    } catch (error) {
      // Error already logged in saveWithoutNavigating
    }
  }, [saveWithoutNavigating, navigateBack]);

  // Handler for search item selection - save changes then navigate
  const handleSearchItemSelect = React.useCallback(async (item: { id: string | number; categoryName?: string }) => {
    const targetItemId = toNavigableReviewItemId(item.id);
    if (targetItemId === null) {
      console.error('[ItemDetailScreen] Search result has an invalid item ID', item.id);
      return;
    }

    setShowSearchModal(false);

    // If user made changes, save first WITHOUT navigating
    if (hasUserInteractedRef.current && !isSavingRef.current) {
      try {
        const didSave = await saveWithoutNavigating();
        if (!didSave) {
          return;
        }
      } catch (error) {
        // Save failed - don't navigate
        console.error('Failed to save before navigation:', error);
        return;
      }
    }

    // Then navigate to the new item
    (navigation as any).replace('ItemDetail', {
      reviewId: numericReviewId,
      itemId: targetItemId,
      categoryName: item.categoryName || 'Item',
    });
  }, [navigation, numericReviewId, saveWithoutNavigating]);

  // Handler for hamburger item press - reuse search item selection logic
  const handleItemPress = React.useCallback((itemId: number, categoryName: string) => {
    handleSearchItemSelect({ id: itemId, categoryName });
  }, [handleSearchItemSelect]);

  // Build hamburger actions from template categories
  const categories = review?.template?.categories || [];
  const hamburgerActions = React.useMemo(
    () => buildHamburgerActions(categories, handleItemPress),
    [categories, handleItemPress]
  );

  const handleSaveAsDraft = React.useCallback(async () => {
    // Prevent saving if unmounted or already saving
    if (!isMountedRef.current || isSavingRef.current) return;

    isSavingRef.current = true;
    setIsSaving(true);

    try {
      // CRITICAL: Filter out question photos to prevent duplication
      const itemLevelPhotosOnly = photos.filter((p: any) =>
        !p.metadata?.questionId && !p.questionId
      );

      // Clean photos to remove non-serializable File objects before saving
      const cleanedPhotos = itemLevelPhotosOnly.map((photo: any) => {
        const { file, ...serializable } = photo;
        return serializable;
      });

      // Clean question photos too
      const cleanedQuestionPhotos: Record<string, any[]> = {};
      Object.entries(questionPhotos).forEach(([key, photoArray]) => {
        cleanedQuestionPhotos[key] = photoArray.map((photo: any) => {
          const { file, ...serializable } = photo;
          return serializable;
        });
      });

      const updatedResponse = {
        isPresent,
        notes,
        responseData: {
          ...responseData,
          ...roomSelections, // Include room selections for multiple items
        },
        roomId,
        floor,
        photos: cleanedPhotos,
        questionPhotos: cleanedQuestionPhotos,
        findings,
        status: ResponseStatus.IN_PROGRESS,
      };

      // Update local state immediately
      dispatch(updateLocalResponse({ reviewId: numericReviewId, itemId: numericItemId, data: updatedResponse }));

      // Ensure SQLite/journal writes finish before navigating away from the edit screen.
      const isSubmitted = review?.status === 'submitted';
      await dispatch(
        isSubmitted
          ? updateSubmittedReviewResponse({ reviewId: numericReviewId, itemId: numericItemId, data: updatedResponse })
          : updateReviewResponse({ reviewId: numericReviewId, itemId: numericItemId, data: updatedResponse })
      ).unwrap().catch(() => {
        syncFailureCountRef.current += 1;
      });

      // Navigate immediately after optimistic update
      // Clear saving state before navigation to stay responsive even if navigation cancels
      if (isMountedRef.current && !isNavigatingRef.current) {
        isSavingRef.current = false;
        setIsSaving(false);
        clearUserInteracted();
        navigateBack();
      }
    } finally {
      // Reset saving state if navigation didn't happen (e.g., synchronous error)
      isSavingRef.current = false;
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [photos, questionPhotos, findings, isPresent, notes, responseData, roomSelections, roomId, floor, numericItemId, dispatch, numericReviewId, review?.status, navigateBack, clearUserInteracted]);

  // Set up navigation header with explicit back to review detail
  React.useLayoutEffect(() => {
    navigation.setOptions({
      title: categoryName,
      headerLeft: () => (
        <TouchableOpacity
          onPress={() => {
            // Check if we need to save
            if (hasUserInteractedRef.current && !isSavingRef.current && !isAutoSavingRef.current) {
              handleAutoSaveAndNavigate();
            } else {
              // Always navigate back to the review detail screen
              const state = navigation.getState();
              const isFromResults = (state.routeNames as string[] | undefined)?.includes('ResultsList');

              if (isFromResults) {
                (navigation as any).navigate('ResultDetail', { reviewId: numericReviewId });
              } else {
                (navigation as any).navigate('ReviewDetail', { reviewId: numericReviewId });
              }
            }
          }}
          style={{ paddingHorizontal: 10 }}
        >
          <Text style={{ fontSize: 24, color: colors.primary }}>←</Text>
        </TouchableOpacity>
      ),
    });
  }, [navigation, categoryName, handleAutoSaveAndNavigate, numericReviewId]);

  // Early return AFTER all hooks are declared to follow Rules of Hooks  
  if (!review) {
    return (
      <View testID="item-detail-screen" style={styles.container}>
        <View style={styles.emptyState}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.emptyText}>
            {isOnline ? 'Loading review...' : 'Review is not available on this device yet.'}
          </Text>
        </View>
      </View>
    );
  }

  // Wrap the entire render in try-catch to prevent iOS crashes
  try {
    return (
      <View style={styles.container}>
        <ScrollView 
          testID="item-detail-scroll"
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        >
        <View
          testID="item-detail-header"
          style={[
            styles.headerCardContainer,
            itemFindingAppearance && styles.findingHeader,
            itemFindingAppearance && {
              backgroundColor: itemFindingAppearance.surface,
              borderLeftColor: itemFindingAppearance.border,
            },
          ]}
        >
          <Card
            style={[
              styles.headerCard,
              itemFindingAppearance && { backgroundColor: itemFindingAppearance.surface },
            ]}
          >
            <Card.Content>
              <View style={styles.headerRow}>
                <View style={styles.headerTextContainer}>
                  <Text
                    testID="item-detail-name"
                    style={[
                      styles.itemName,
                      itemFindingAppearance && { color: itemFindingAppearance.foreground },
                    ]}
                  >
                    {item.name}
                  </Text>
                  {item.description && (
                    <Text
                      testID="item-detail-description"
                      style={[
                        styles.itemDescription,
                        itemFindingAppearance && { color: itemFindingAppearance.foreground },
                      ]}
                    >
                      {item.description}
                    </Text>
                  )}
                </View>
                {item.isOnDemand && (
                  <Button
                    mode="outlined"
                    icon="pencil"
                    onPress={() => setShowQuestionEditor(true)}
                    style={styles.editQuestionsButton}
                    textColor={itemFindingAppearance?.foreground}
                  >
                    Edit Questions
                  </Button>
                )}
              </View>
            </Card.Content>
          </Card>
        </View>

        {itemLoadError && (
          <Text style={styles.errorText}>{itemLoadError}</Text>
        )}

        {isItemLoading && (
          <View style={styles.inlineLoader}>
            <ActivityIndicator size="small" color={colors.primary} />
            <Text style={styles.loadingLabel}>Refreshing item data...</Text>
          </View>
        )}

        {/* Present/Not Present Toggle */}
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.sectionTitle}>Is this item present in the home?</Text>
            <RadioButton.Group
              onValueChange={value => {
                setIsPresent(value === 'true');
                markUserInteracted();
              }}
              value={isPresent.toString()}
            >
              <List.Item
                testID="item-present-yes"
                title="Yes, this item is present"
                left={() => <RadioButton value="true" />}
                onPress={() => {
                  setIsPresent(true);
                  markUserInteracted();
                }}
              />
              <List.Item
                testID="item-present-no"
                title="No, this item is not present"
                left={() => <RadioButton value="false" />}
                onPress={() => {
                  setIsPresent(false);
                  markUserInteracted();
                }}
              />
            </RadioButton.Group>
          </Card.Content>
        </Card>

        {/* Dynamic Questions */}
        {isPresent && item.questionsSchema && (
          <Card style={styles.card}>
            <Card.Content>
              <Text style={styles.sectionTitle}>Item Details</Text>
              <DynamicForm
                schema={item.questionsSchema}
                data={responseData}
                onChange={(data) => {
                  setResponseData(data);
                  markUserInteracted();
                }}
                reviewId={numericReviewId}
                responseId={localResponseId || response?.id || 0}
                responseTempId={currentResponseTempId || response?.tempId}
                itemId={numericItemId}
                initialPhotos={presentationQuestionPhotos}
                onPhotosChange={handleQuestionPhotosChange}
                itemName={item.name}
                propertyId={review?.propertyId}
                roomId={roomId}
                floor={floor}
                onRoomSelect={handleRoomSelect}
                onFloorSelect={handleFloorSelect}
                autoFocusOnMount={false}
                requiresLocation={item.requiresLocation !== false}
                requiresFloor={item.requiresFloor !== false}
                findingQuestionIds={findingClassification.mode === 'questions'
                  ? findingClassification.questionIds || []
                  : []}
                findings={findings}
                onFindingsChange={(nextFindings) => {
                  setFindings(nextFindings);
                  markUserInteracted();
                }}
              />
            </Card.Content>
          </Card>
        )}

        {findingClassification.mode === 'item' && (
          <Card style={styles.card}>
            <Card.Content>
              <Text style={styles.sectionTitle}>Finding</Text>
              <FindingEditor
                questionId={null}
                finding={getFinding(findings, null)}
                label="Flag this item as a finding"
                onChange={(finding) => {
                  setFindings(replaceFinding(findings, null, finding));
                  markUserInteracted();
                }}
              />
            </Card.Content>
          </Card>
        )}

        {/* Notes */}
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.sectionTitle}>Additional Notes</Text>
            <StylusTextInput
              testID="item-notes-input"
              mode="outlined"
              multiline
              numberOfLines={4}
              value={notes}
              onChangeText={(text) => {
                setNotes(text);
                markUserInteracted();
              }}
              placeholder="Enter any additional notes..."
              style={styles.notesInput}
              label="Notes"
              autoFocusOnMount={false}
              blurOnSubmit={shouldSkipOfflineCache}
              returnKeyType={shouldSkipOfflineCache ? 'done' : undefined}
            />
          </Card.Content>
        </Card>

        {/* Photo Capture - With proper iOS permissions */}
        <Card style={styles.card}>
          <Card.Content>
            {canQueuePhotos ? (
              <PhotoCapture
                reviewId={numericReviewId}
                responseId={photoResponseId}
                responseTempId={photoResponseTempId}
                itemId={numericItemId}
                initialPhotos={presentationPhotos}
                onPhotosChange={handlePhotosChange}
                questionLabel={item.name}
                maxPhotos={MAX_PHOTOS_PER_UPLOAD}
                // No questionId for item-level photos
              />
            ) : (
              <View testID="photo-response-pending" style={styles.photoPendingState}>
                <ActivityIndicator size="small" color={colors.primary} />
              </View>
            )}
          </Card.Content>
        </Card>

        {/* Action Buttons */}
        <View style={styles.buttonContainer}>
          <Button
            testID="save-item-draft-button"
            mode="outlined"
            onPress={handleSaveAsDraft}
            style={styles.button}
            loading={isSaving}
            disabled={isSaving}
          >
            Save as Draft
          </Button>
          <Button
            testID="complete-item-button"
            mode="contained"
            onPress={handleSave}
            style={styles.button}
            loading={isSaving}
            disabled={isSaving}
          >
            Complete Item
          </Button>
        </View>
      </ScrollView>

      {isDetoxTestControlsEnabled && (
        <View style={styles.detoxItemControlPanel} pointerEvents="box-none">
          <Button
            testID="detox-complete-item-button"
            mode="contained"
            onPress={handleSave}
            style={styles.detoxItemControlButton}
            loading={isSaving}
            disabled={isSaving}
          >
            Complete
          </Button>
        </View>
      )}
      
      {/* Question List Modal for on-demand items */}
      {item.isOnDemand && (
        <QuestionListModal
          visible={showQuestionEditor}
          onDismiss={() => setShowQuestionEditor(false)}
          onSave={handleUpdateQuestions}
          initialQuestions={item.questionsSchema?.fields || []}
          title={`Edit Questions for ${item.name}`}
        />
      )}

      {/* Expandable FAB for quick actions */}
      <ExpandableFAB
        actions={[
          {
            id: 'search',
            icon: 'magnify',
            label: 'Search',
            onPress: () => setShowSearchModal(true),
          },
          {
            id: 'general-notes',
            icon: 'note-text',
            label: 'General Notes',
            onPress: () => setShowGeneralNotesModal(true),
          },
          {
            id: 'on-demand-projects',
            icon: 'hammer-wrench',
            label: 'On Demand Projects',
            onPress: () => setShowOnDemandProjectsModal(true),
          },
          ...hamburgerActions,
        ]}
        style={styles.fab}
      />

      {/* General Notes Modal */}
      <GeneralNotesModal
        visible={showGeneralNotesModal}
        onDismiss={() => setShowGeneralNotesModal(false)}
        onSave={handleSaveGeneralNotes}
        initialNotes={review?.notes || ''}
        loading={isSavingNotes}
      />

      {/* On Demand Projects Modal */}
      <OnDemandProjectsModal
        visible={showOnDemandProjectsModal}
        onDismiss={() => setShowOnDemandProjectsModal(false)}
        onSave={handleSaveOnDemandProjects}
        initialOnDemandProjects={review?.onDemandProjects || ''}
        loading={isSavingOnDemandProjects}
      />

      {/* Search Modal */}
      <ReviewSearchModal
        visible={showSearchModal}
        onDismiss={() => setShowSearchModal(false)}
        reviewId={numericReviewId}
        onItemSelect={handleSearchItemSelect}
        review={review}
        additionalItems={onDemandItems}
      />

      {/* Auto-save overlay */}
      {isAutoSaving && (
        <View style={styles.autoSaveOverlay}>
          <View style={styles.autoSaveContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={styles.autoSaveText}>Saving draft...</Text>
          </View>
        </View>
      )}
    </View>
  );
  } catch (renderError) {
    console.error('[ItemDetailScreen] Render error:', renderError);
    // Return a simple error screen
    return (
      <View style={styles.container}>
        <View style={styles.emptyState}>
          <Text style={{ fontSize: 48, color: colors.error }}>⚠️</Text>
          <Text style={styles.emptyText}>Unable to load item details</Text>
          <Button
            mode="contained"
            onPress={() => {
              try {
                navigation.goBack();
              } catch (navError) {
                console.error('[ItemDetailScreen] Navigation back error:', navError);
              }
            }}
            style={{ marginTop: spacing.md }}
          >
            Go Back
          </Button>
        </View>
      </View>
    );
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollContent: {
    paddingBottom: spacing.xxl,
  },
  headerCardContainer: {
    margin: spacing.md,
    borderRadius: 12,
  },
  headerCard: {
    ...shadows.md,
  },
  findingHeader: {
    borderLeftWidth: 6,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerTextContainer: {
    flex: 1,
    marginRight: spacing.md,
  },
  itemName: {
    ...typography.h3,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  itemDescription: {
    ...typography.body2,
    color: colors.textSecondary,
  },
  errorText: {
    ...typography.body2,
    color: colors.error,
    marginHorizontal: spacing.md,
    marginTop: spacing.sm,
  },
  inlineLoader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: spacing.md,
    marginTop: spacing.sm,
  },
  loadingLabel: {
    ...typography.caption,
    color: colors.textSecondary,
    marginLeft: spacing.sm,
  },
  editQuestionsButton: {
    marginTop: spacing.xs,
  },
  requiredBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.sm,
  },
  requiredText: {
    ...typography.caption,
    color: colors.error,
    marginLeft: spacing.xs,
    fontWeight: '600',
  },
  card: {
    marginHorizontal: spacing.md,
    marginBottom: spacing.md,
    ...shadows.sm,
  },
  sectionTitle: {
    ...typography.h4,
    color: colors.text,
    marginBottom: spacing.md,
  },
  notesInput: {
    backgroundColor: colors.background,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
  },
  button: {
    flex: 1,
    marginHorizontal: spacing.xs,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: spacing.xl,
  },
  emptyText: {
    ...typography.body1,
    color: colors.textSecondary,
    marginTop: spacing.sm,
    textAlign: 'center',
  },
  photoPendingState: {
    minHeight: 96,
    alignItems: 'center',
    justifyContent: 'center',
  },
  autoSaveOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  autoSaveContainer: {
    backgroundColor: colors.background,
    padding: spacing.lg,
    borderRadius: 8,
    alignItems: 'center',
    minWidth: 150,
    ...shadows.md,
  },
  autoSaveText: {
    ...typography.body1,
    color: colors.text,
    marginTop: spacing.sm,
    textAlign: 'center',
  },
  detoxItemControlPanel: {
    position: 'absolute',
    top: spacing.sm,
    right: spacing.md,
    zIndex: 20,
  },
  detoxItemControlButton: {
    minWidth: 112,
  },
  fab: {
    // ExpandableFAB component handles its own positioning
  },
});

// Wrapped component with error boundary
const ItemDetailScreen: React.FC = () => {
  const navigation = useNavigation();
  
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error('[ItemDetailScreen] Error caught:', error);
        console.error('[ItemDetailScreen] Error info:', errorInfo);
      }}
      fallback={
        <View style={styles.container}>
          <View style={styles.emptyState}>
            <Text style={{ fontSize: 48, color: colors.error }}>⚠️</Text>
            <Text style={styles.emptyText}>Unable to load item details</Text>
            <Button
              mode="contained"
              onPress={() => navigation.goBack()}
              style={{ marginTop: spacing.md }}
            >
              Go Back
            </Button>
          </View>
        </View>
      }
    >
      <ItemDetailScreenContent />
    </ErrorBoundary>
  );
};

export default ItemDetailScreen;
